import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../../app/hooks";
import { Loading } from "../../../../components/Loading";
import { useAdminReportsFilters } from "../useAdminReportsFilters";
import { buildReportQueryString } from "../adminReportsUtil";
import { AdminReportsResponse } from "../adminReportsApiSlice";
import { DistrictSchoolFilters } from "../DistrictSchoolFilters";
import { TableRightSideHeader } from "../TableRightSideHeader";
import {
	HolisticAndTraitScoresByGenreRow,
	useLazyHolisticAndTraitScoresByGenreQuery,
} from "./adminPerformanceReportApiSlice";
import { HolisticAndTraitScoresByGenreTable } from "./HolisticAndTraitByGenreScoresTable";

export default function HolisticAndTraitScoresByGenre() {
	const { aspUrl } = useAppSelector((state) => state.site);
	const {
		districtOptions,
		schoolOptions,
		districtFilter,
		schoolFilter,
		setDistrictFilter,
		setSchoolFilter,
	} = useAdminReportsFilters();

	const navigate = useNavigate();

	const [data, setData] = useState<AdminReportsResponse<HolisticAndTraitScoresByGenreRow>>();
	const [lastUpdated, setLastUpdated] = useState<Date>();

	const [callHolisticAndTraitScoresByGenre] = useLazyHolisticAndTraitScoresByGenreQuery();

	// Filter out "All Districts" and "All Schools" options
	const filteredDistrictOptions = useMemo(
		() => districtOptions.filter((opt) => opt.value !== 0),
		[districtOptions]
	);
	const filteredSchoolOptions = useMemo(
		() => schoolOptions.filter((opt) => opt.value !== 0),
		[schoolOptions]
	);

	// Auto-select first district when district is 0 (All Districts) on a refresh of the page
	useEffect(() => {
		if (districtFilter === 0 && filteredDistrictOptions.length > 0) {
			setDistrictFilter(filteredDistrictOptions[0].value as number);
		}
	}, [filteredDistrictOptions]);

	// Auto-select first school when district changes or when school is 0 (All Schools)
	useEffect(() => {
		if (schoolFilter === 0 && filteredSchoolOptions.length > 0) {
			setSchoolFilter(filteredSchoolOptions[0].value as number);
		}
	}, [filteredSchoolOptions]);

	useEffect(() => {
		// skip if don't have the necessary ids for the report
		if (!districtFilter || !schoolFilter) {
			return;
		}

		callHolisticAndTraitScoresByGenre(
			{
				districtId: districtFilter === 0 ? undefined : districtFilter,
				schoolId: schoolFilter === 0 ? undefined : schoolFilter,
			},
			true //cache the results
		)
			.unwrap()
			.then((payload) => {
				setData(payload.response);
				setLastUpdated(payload.lastUpdateDate);
			})
			.catch((error) => {
				console.log(error.data);
				navigate("/adminReports");
			});
	}, [districtFilter, schoolFilter]);

	const queryString = useMemo(() => {
		return buildReportQueryString({
			districtFilter,
			schoolFilter,
		});
	}, [districtFilter, schoolFilter]);

	return (
		<section>
			<h1>School Holistic and Trait Scores by Genre</h1>
			<p className="mt-4 print:hidden">
				The Holistic and Trait Scores by Genre Report displays the average scores for each
				genre for all courses. The averages are calculated using the most recent draft score
				of each essay. Student count represents how many different students have written to
				that genre. If the student count for any row is more than 10, the percentile and
				stanine ranks are determined. Click the three dots to print or download.
			</p>
			<div className="mt-3">
				<Loading loading={!data} />
				{data && (
					<>
						<div className="flex justify-between items-end">
							<DistrictSchoolFilters
								districtOptions={filteredDistrictOptions}
								schoolOptions={filteredSchoolOptions}
								districtFilter={districtFilter}
								schoolFilter={schoolFilter}
								setDistrictFilter={setDistrictFilter}
								setSchoolFilter={setSchoolFilter}
							/>
							<TableRightSideHeader
								lastUpdated={lastUpdated}
								hasData={data.rows.length !== 0}
								uniqueKey="holistic-trait-by-genre-scores"
								downloadExcelLink={`${aspUrl}/AdminReports/HolisticAndTraitScoresByGenreExcel?${queryString}`}
								downloadCsvLink={`${aspUrl}/AdminReports/HolisticAndTraitScoresByGenreCsv?${queryString}`}
							/>
						</div>

						<HolisticAndTraitScoresByGenreTable className="mt-4" data={data} />
					</>
				)}
			</div>
		</section>
	);
}
